import classNames from 'classnames';
import React from 'react';

import { Box, Button, Container, Grid, Typography } from '@material-ui/core';

import { Image } from '../components';
import { useIsMobile, useScreenIsSmallerThan } from '../hooks/useBreakpoints';
import { makeBlockStyles, useBlockStyles } from '../hooks/useStyles';
import { BlockBuilder, BlockHero } from '../types/_blocks.types';
import { normaliseUrl } from '../utils/url';

const useStyles = makeBlockStyles(() => ({
  root: {
    color: 'white',
    height: '100%',
    padding: '1rem 2rem',
    [`@media screen and (max-width:768px)`]: {
      height: 'initial',
    },
  },
  fullHeight: {
    height: '100vh',
    paddingTop: '10vh',
    position: 'relative',
    zIndex: 0,
    [`@media screen and (max-width:768px)`]: {
      minHeight: '100vh',
      display: 'flex',
      height: '100%',
    },
    [`@media screen and (max-width:360px)`]: {
      minHeight: '100%',
    },
  },
  overlay: {
    backgroundColor: 'rgba(0,0,0,0.2)',
    height: '100%',
    left: '65%',
    position: 'absolute',
    top: 0,
    width: '35%',
    zIndex: -1,
    '&::after': {
      color: 'rgba(255,255,255,0.25)',
      content: '"32,000ft"',
      display: 'inline-block',
      transform: 'rotate(90deg)',
      position: 'absolute',
      right: '2rem',
      fontSize: '4rem',
      fontWeight: 'bold',
      lineHeight: 0,
      margin: '50vh -10vh 0 0',
      padding: 0,
    },
  },
  image: {
    backgroundImage: (props: BlockHero) => `url(${props.image})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'bottom right',
    backgroundSize: 'contain',
    height: '100%',
    [`@media screen and (max-width:768px)`]: {
      minHeight: 'inherit',
      display: 'flex',
    },
  },
  title: {
    fontSize: '40px',
    lineHeight: '48px',
    fontWeight: 600,
    paddingBottom: '24px',
    [`@media screen and (max-width:321px)`]: {
      fontSize: '28px',
      lineHeight: '32px',
      paddingBottom: '16px',
    },
  },
  container: {
    height: '100%',
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    [`@media screen and (max-width:767px)`]: {
      justifyContent: 'flex-start',
    },
  },
  content: {
    fontSize: '18px',
    lineHeight: '28px',
    fontWeight: 400,
    [`@media screen and (max-width:321px)`]: {
      fontSize: '16px',
    },
  },
  footerWrapper: {
    height: '100%',
    flexWrap: 'nowrap',
  },
  footer: {
    minHeight: '100%',
    justifyContent: 'space-between',
    flexFlow: 'column-reverse nowrap',
    padding: '0 8px',
  },
  buttonWrapper: {
    marginBottom: '18px',
    [`@media screen and (max-width:321px)`]: {
      marginBottom: '12px',
    },
  },
  button: {
    border: '1px solid #081720',
  },
  helpText: {
    textAlign: 'center',
    fontSize: '13px',
    lineHeight: '28px',
    color: '#12394F',
  },
  subtitleHomePage: {
    paddingTop: '24px',
  },
  subtitle: {
    paddingTop: '32px',
    [`@media screen and (max-width:321px)`]: {
      paddingTop: '24px',
    },
  },
  brands: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    width: '100%',
    padding: '24px 0',
  },
  brandsHomePage: {
    marginTop: '16px',
  },
  brandsItems: {
    maxWidth: '25%',
    flex: 1,
  },
}));

type Props = BlockBuilder<BlockHero>;

export const HeroBlock: React.FC<Props> = ({ block, getImageData }) => {
  const {
    title,
    image,
    content,
    ctas,
    subtitle,
    items,
    align,
    backgroundImage,
    isHomePage,
    helpText,
  } = block;
  const isMobile = useIsMobile();
  const isSmallMobile = useScreenIsSmallerThan(361);

  const hasImage = !!image && (!isHomePage || !isMobile) && !isSmallMobile;

  const heroImageData = backgroundImage
    ? getImageData(backgroundImage)
    : getImageData('hero/hero-dog.png');

  if (!heroImageData) {
    console.warn('Could not resolve hero image');
  }

  const classes = useBlockStyles(useStyles, {
    ...block,
    image: heroImageData?.images.fallback?.src,
  });

  return (
    <>
      {(isHomePage || isMobile) && (
        <Box>
          <Box
            className={classNames(
              classes.boxBlock,
              !!image && classes.fullHeight,
            )}
          >
            {hasImage && isHomePage && <Box className={classes.overlay}></Box>}
            <Box className={classNames(hasImage && classes.image)}>
              <Container
                style={{ textAlign: align ?? 'initial' }}
                className={classes.root}
              >
                <Grid
                  container
                  justifyContent="flex-start"
                  className={classes.container}
                >
                  <Grid
                    {...(isHomePage && {
                      container: true,
                      item: true,
                      spacing: 4,
                      md: hasImage ? 7 : 12,
                    })}
                    className={classes.wrapper}
                  >
                    <Grid
                      item
                      xs={12}
                      style={{ paddingTop: '3.5rem', flexBasis: 0 }}
                    >
                      <Typography
                        variant="h3"
                        className={classNames(!isHomePage && classes.title)}
                      >
                        {title}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} style={{ flexBasis: 0 }}>
                      <Typography
                        variant="body1"
                        className={classNames(!isHomePage && classes.content)}
                      >
                        {content}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      container
                      spacing={4}
                      justifyContent={
                        align === 'center'
                          ? 'center'
                          : align === 'right'
                          ? 'flex-end'
                          : 'flex-start'
                      }
                      className={classNames(
                        !isHomePage && classes.footerWrapper,
                      )}
                    >
                      <Grid item xs={12}>
                        <Grid
                          container
                          spacing={2}
                          className={classNames(!isHomePage && classes.footer)}
                        >
                          {ctas.map(
                            ({ show, label, link }) =>
                              show != false && (
                                <Grid
                                  key={label}
                                  {...(isHomePage && { item: true })}
                                  {...(isMobile && { xs: 12 })}
                                  className={classNames(
                                    !isHomePage && classes.buttonWrapper,
                                  )}
                                >
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    href={normaliseUrl(link)}
                                    fullWidth={isMobile}
                                    className={classNames(
                                      !isHomePage && classes.button,
                                    )}
                                  >
                                    {label}
                                  </Button>
                                  {!isHomePage && helpText && (
                                    <Typography
                                      variant="body1"
                                      className={classes.helpText}
                                    >
                                      {helpText}
                                    </Typography>
                                  )}
                                </Grid>
                              ),
                          )}

                          <Grid
                            style={
                              !isHomePage
                                ? { flexFlow: 'column nowrap' }
                                : { padding: '0 8px' }
                            }
                            container
                          >
                            <Grid
                              {...(isHomePage && { item: true, xs: 12 })}
                              style={{ opacity: 0.7 }}
                              className={classNames(
                                isHomePage
                                  ? classes.subtitleHomePage
                                  : classes.subtitle,
                              )}
                            >
                              <div
                                dangerouslySetInnerHTML={{ __html: subtitle }}
                              />
                            </Grid>
                            <Grid
                              {...(isHomePage && {
                                item: true,
                                container: true,
                                spacing: 4,
                                justifyContent:
                                  align === 'center'
                                    ? 'center'
                                    : align === 'right'
                                    ? 'flex-end'
                                    : 'flex-start',
                              })}
                              style={{ opacity: 0.5 }}
                              className={classNames(
                                isHomePage
                                  ? classes.brandsHomePage
                                  : classes.brands,
                              )}
                            >
                              {items.map((item, idx) => (
                                <Grid
                                  item
                                  key={`${item.image}_${idx}`}
                                  md={3}
                                  className={classNames(
                                    !isHomePage && classes.brandsItems,
                                  )}
                                >
                                  <Image
                                    getImageData={getImageData}
                                    src={item.image}
                                    alt={item.alt}
                                    size="contain"
                                    isBackground={true}
                                    options={{
                                      height: '100%',
                                      width: 'auto',
                                    }}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};
